import React from 'react';
import { TrendingUp, Target, Calendar as CalendarIcon, CheckCircle2 } from 'lucide-react';
import type { SelfServiceSubmissionRow } from '../../types/selfService';
import { apiClient } from '../../lib/apiClient';
import { REPORT_ACCENT } from './reportTheme';
import { rangeQuery } from './reportRange';

// CTV-445: campaign performance summary — the four headline stats (Total plays,
// Completion rate, Daily avg, Goal). Split out of the delivery card so they sit
// at the top of the report, right under the insights band. These honor the
// global date-range filter (from/to), same as the rest of the report. Derived
// from the same /submissions/:id/delivery-daily payload the delivery card uses,
// so there's no new endpoint.

interface DailyDatum {
  date: string;
  plays: number;
  completes?: number; // absent on legacy responses
}

interface FlightSummary {
  pacing_impressions: number;
}

interface CampaignStatsBandProps {
  row: SelfServiceSubmissionRow;
  accentColor?: string;
  secondaryColor?: string;
  from?: string | null;
  to?: string | null;
  rangeLabel?: string | null;
  // Live (still delivering) → the Goal box shows a pacing status caption.
  live?: boolean;
}

const CampaignStatsBand: React.FC<CampaignStatsBandProps> = ({ row, accentColor, secondaryColor, from, to, rangeLabel, live }) => {
  const accent = accentColor || REPORT_ACCENT;
  const stripe = secondaryColor || accent;
  const [daily, setDaily] = React.useState<DailyDatum[] | null>(null);
  const [flights, setFlights] = React.useState<FlightSummary[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    // Keep the current stats on screen while the new range loads (dimmed), so
    // switching ranges doesn't flash the skeleton back in.
    setLoading(true);
    apiClient
      .get<{ daily: DailyDatum[]; flights?: FlightSummary[] }>(
        `/submissions/${row.id}/delivery-daily${rangeQuery({ from: from ?? null, to: to ?? null })}`,
      )
      .then((d) => {
        if (cancelled) return;
        setDaily(d.daily ?? []);
        setFlights(d.flights ?? []);
      })
      .catch(() => {
        if (!cancelled) setDaily([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [row.id, from, to]);

  // Skeleton only on the very first load; range changes keep the prior stats.
  if (daily === null) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-200/70">
          <SkeletonStat />
          <SkeletonStat />
          <SkeletonStat />
          <SkeletonStat />
        </div>
      </div>
    );
  }

  const rows = daily;
  const totalPlays = rows.reduce((a, d) => a + d.plays, 0);
  // Self-hide when there's no delivery — matches the insights band / trends
  // above; the delivery card below carries the "not started yet" empty state.
  if (totalPlays === 0) return null;

  // Lifetime video completion rate = completes / plays. Null (renders "—") when
  // the response carries no completes data at all (legacy responses omit the
  // field, and we must not show that as a misleading 0%).
  const hasCompletes = rows.some((d) => d.completes != null);
  const totalCompletes = rows.reduce((a, d) => a + (d.completes ?? 0), 0);
  const completionRate =
    hasCompletes && totalPlays > 0 ? Math.min(100, Math.round((totalCompletes / totalPlays) * 100)) : null;

  const dailyAvg = rows.length > 0 ? Math.round(totalPlays / rows.length) : 0;

  // Goal denominator: sum of every flight's pacing target, falling back to the
  // submission-level pacing when flights weren't returned (legacy responses).
  const pacing =
    flights.length > 0
      ? flights.reduce((a, f) => a + (f.pacing_impressions ?? 0), 0)
      : row.pacing_impressions ?? null;
  const goalPct = pacing != null && pacing > 0 ? Math.min(999, Math.round((totalPlays / pacing) * 100)) : null;

  // Pacing status for a live campaign (only on the full, unfiltered view, where
  // totalPlays is lifetime). Compares delivery so far against what the schedule
  // expects by now: pacing × (elapsed / total flight days).
  const paceCaption = ((): string | null => {
    if (!live || from || to || pacing == null || pacing <= 0) return null;
    const startStr = row.campaign?.startDate?.slice(0, 10);
    const endStr = row.campaign?.endDate?.slice(0, 10);
    if (!startStr || !endStr) return null;
    const start = new Date(`${startStr}T00:00:00`).getTime();
    const end = new Date(`${endStr}T23:59:59`).getTime();
    if (!(end > start)) return null;
    const elapsed = Math.min(1, Math.max(0, (Date.now() - start) / (end - start)));
    const expected = pacing * elapsed;
    if (expected <= 0) return null;
    const ratio = totalPlays / expected;
    return ratio >= 1.05 ? 'Ahead of pace' : ratio >= 0.9 ? 'On pace' : 'Behind pace';
  })();

  return (
    <div
      className={`rounded-2xl border border-slate-200/80 bg-white overflow-hidden transition-opacity duration-300 ${loading ? 'opacity-60' : 'opacity-100'}`}
    >
      <div className="h-[3px]" style={{ backgroundColor: stripe }} />
      <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-200/70">
        <ReportStat icon={TrendingUp} label="Total plays" value={totalPlays.toLocaleString('en-US')} accent={accent} caption={rangeLabel} />
        <ReportStat
          icon={CheckCircle2}
          label="Completion rate"
          value={completionRate == null ? '—' : `${completionRate}%`}
          accent={accent}
          caption={rangeLabel}
        />
        <ReportStat icon={CalendarIcon} label="Daily avg" value={dailyAvg.toLocaleString('en-US')} accent={accent} caption={rangeLabel} />
        <ReportStat
          icon={Target}
          label="Goal"
          value={goalPct == null ? '—' : `${goalPct}%`}
          accent={accent}
          valueAccent={goalPct != null && goalPct >= 100 ? accent : undefined}
          caption={paceCaption ?? rangeLabel}
        />
      </div>
    </div>
  );
};

const SkeletonStat: React.FC = () => (
  <div className="px-5 py-4 flex items-start gap-3 min-w-0">
    <div className="w-7 h-7 rounded-full bg-slate-200 animate-pulse shrink-0 mt-0.5" />
    <div className="min-w-0 flex-1">
      <div className="h-2 w-16 rounded bg-slate-200 animate-pulse" />
      <div className="h-4 w-20 rounded bg-slate-200 animate-pulse mt-2" />
    </div>
  </div>
);

const ReportStat: React.FC<{
  icon: React.FC<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  value: string;
  accent: string;
  valueAccent?: string;
  // Small qualifier under the value (e.g. "Last 7 days") shown when the report
  // is scoped by the date-range filter, so the number doesn't read as lifetime.
  caption?: string | null;
}> = ({ icon: Icon, label, value, accent, valueAccent, caption }) => (
  <div className="px-5 py-4 flex items-start gap-3 min-w-0">
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5"
      style={{ backgroundColor: `${accent}14`, color: accent }}
    >
      <Icon className="w-3.5 h-3.5" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">{label}</div>
      <div className="flex items-baseline gap-1.5 mt-0.5 min-w-0">
        <span
          className="text-[18px] font-semibold tracking-tight shrink-0"
          style={{ color: valueAccent ?? 'rgb(15, 23, 42)' }}
        >
          {value}
        </span>
        {caption && <span className="text-[10px] text-slate-400 truncate">{caption}</span>}
      </div>
    </div>
  </div>
);

export default CampaignStatsBand;
